if (!customElements.get('cart-cross-sell')) {
  class CartCrossSell extends HTMLElement {
    constructor() {
      super();
      this.handleAddToCart = this.handleAddToCart.bind(this);
      this.handleDragStart = this.handleDragStart.bind(this);
      this.handleDragMove = this.handleDragMove.bind(this);
      this.handleDragEnd = this.handleDragEnd.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDragPrevent = this.handleDragPrevent.bind(this);

      this.isDragging = false;
      this.hasMoved = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragThreshold = 3;

      this.velocityTracker = [];
      this.maxVelocitySamples = 5;
      this.momentumId = null;
      this.clickPreventionTimeout = null;
    }

    connectedCallback() {
      this.productsContainer = this.querySelector('.cart-cross-sell__products');
      this.buttons = this.querySelectorAll('.cart-cross-sell__product-button');

      this.buttons.forEach(button => {
        if (!button.disabled) {
          button.addEventListener('click', this.handleAddToCart);
        }
      });

      if (this.productsContainer) {
        this.productsContainer.addEventListener('mousedown', this.handleDragStart, { passive: false });
        this.productsContainer.addEventListener('dragstart', this.handleDragPrevent);
        this.productsContainer.addEventListener('touchstart', this.handleDragStart, { passive: false });
        this.productsContainer.addEventListener('click', this.handleClick, true);
      }
    }

    disconnectedCallback() {
      this.buttons?.forEach(button => {
        button.removeEventListener('click', this.handleAddToCart);
      });

      if (this.productsContainer) {
        this.productsContainer.removeEventListener('mousedown', this.handleDragStart);
        this.productsContainer.removeEventListener('dragstart', this.handleDragPrevent);
        this.productsContainer.removeEventListener('touchstart', this.handleDragStart);
        this.productsContainer.removeEventListener('click', this.handleClick, true);
      }

      this.cleanupDragListeners();
      this.cancelMomentum();

      if (this.clickPreventionTimeout) {
        clearTimeout(this.clickPreventionTimeout);
      }
    }

    cleanupDragListeners() {
      document.removeEventListener('mousemove', this.handleDragMove);
      document.removeEventListener('mouseup', this.handleDragEnd);
      document.removeEventListener('touchmove', this.handleDragMove);
      document.removeEventListener('touchend', this.handleDragEnd);
      document.removeEventListener('touchcancel', this.handleDragEnd);
    }

    cancelMomentum() {
      if (this.momentumId) {
        cancelAnimationFrame(this.momentumId);
        this.momentumId = null;
      }
    }

    handleClick(event) {
      if (this.hasMoved) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    handleDragPrevent(event) {
      event.preventDefault();
    }

    getPointerX(event) {
      return event.type.startsWith('touch') ? event.touches[0].pageX : event.pageX;
    }

    handleDragStart(event) {
      const target = event.target;
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        return;
      }

      this.cancelMomentum();

      this.isDragging = true;
      this.hasMoved = false;
      this.velocityTracker = [];

      this.startX = this.getPointerX(event);
      this.currentX = this.startX;

      document.addEventListener('mousemove', this.handleDragMove, { passive: false });
      document.addEventListener('mouseup', this.handleDragEnd, { passive: false });
      document.addEventListener('touchmove', this.handleDragMove, { passive: false });
      document.addEventListener('touchend', this.handleDragEnd, { passive: false });
      document.addEventListener('touchcancel', this.handleDragEnd, { passive: false });

      this.productsContainer.classList.add('is-dragging');
    }

    handleDragMove(event) {
      if (!this.isDragging) return;

      const prevX = this.currentX;
      this.currentX = this.getPointerX(event);
      const movementX = this.currentX - prevX;
      const totalDelta = this.currentX - this.startX;

      if (Math.abs(totalDelta) > this.dragThreshold && !this.hasMoved) {
        this.hasMoved = true;
      }

      if (this.hasMoved) {
        event.preventDefault();

        const now = performance.now();
        this.velocityTracker.push({
          position: this.currentX,
          time: now
        });

        if (this.velocityTracker.length > this.maxVelocitySamples) {
          this.velocityTracker.shift();
        }

        this.productsContainer.scrollLeft -= movementX;
      }
    }

    handleDragEnd() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.cleanupDragListeners();
      this.productsContainer.classList.remove('is-dragging');

      if (this.hasMoved) {
        const velocity = this.calculateVelocity();
        if (Math.abs(velocity) > 0.05) {
          this.applyMomentum(velocity);
        } else {
          this.enableScrollSnap();
        }

        if (this.clickPreventionTimeout) {
          clearTimeout(this.clickPreventionTimeout);
        }
        this.clickPreventionTimeout = setTimeout(() => {
          this.hasMoved = false;
        }, 100);
      } else {
        this.enableScrollSnap();
      }
    }

    calculateVelocity() {
      if (this.velocityTracker.length < 2) {
        return 0;
      }

      const first = this.velocityTracker[0];
      const last = this.velocityTracker[this.velocityTracker.length - 1];
      const timeDelta = last.time - first.time;

      if (timeDelta === 0) {
        return 0;
      }

      return (last.position - first.position) / timeDelta;
    }

    applyMomentum(initialVelocity) {
      let velocity = initialVelocity;
      const deceleration = 0.92;
      const minVelocity = 0.05;
      const maxScrollLeft = this.productsContainer.scrollWidth - this.productsContainer.clientWidth;

      const animate = () => {
        velocity *= deceleration;

        if (Math.abs(velocity) < minVelocity) {
          this.enableScrollSnap();
          return;
        }

        const newScrollLeft = this.productsContainer.scrollLeft - velocity * 16;

        if (newScrollLeft < 0 || newScrollLeft > maxScrollLeft) {
          velocity *= 0.5;

          if (Math.abs(velocity) < minVelocity * 2) {
            this.enableScrollSnap();
            return;
          }
        }

        this.productsContainer.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft));
        this.momentumId = requestAnimationFrame(animate);
      };

      this.momentumId = requestAnimationFrame(animate);
    }

    enableScrollSnap() {
      this.cancelMomentum();
      requestAnimationFrame(() => {
        this.productsContainer.classList.remove('is-dragging');
      });
    }

    async handleAddToCart(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;

      if (!variantId || button.disabled || button.classList.contains('loading')) {
        return;
      }

      button.classList.add('loading');
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          ...theme.utils.fetchConfig(),
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: 'cart-drawer,cart-icon-bubble'
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const item = data.items ? data.items[0] : data;

        theme.pubsub.publish(theme.pubsub.events.CART_UPDATE, {
          item_count: data.item_count,
          total_price: data.total_price,
          sections: data.sections
        });
        theme.pubsub.publish(theme.pubsub.events.CART_ADD, {
          items: [item],
          sections: data.sections
        });
      } catch (error) {
        console.error('Error adding product to cart:', error);
      } finally {
        button.classList.remove('loading');
        button.disabled = false;
      }
    }
  }

  customElements.define('cart-cross-sell', CartCrossSell);
}
